import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Sparkles, ChefHat, Martini, Wand2, RefreshCw, Save, AlertTriangle, Check, X, Search, Lightbulb,
} from "lucide-react";
import api, { formatApiError } from "../api";
import { Button, Input, Label, Select, Card, Badge, Spinner, EmptyState } from "../components/ui/Primitives";
import {
  RECIPE_CATEGORY_LABELS, COCKTAIL_CATEGORY_LABELS, UNIT_LABELS, formatNumber,
} from "../utils/format";

const RECIPE_CATS = ["Vorspeise", "Suppe", "Hauptgang", "Dessert", "Snack", "Fruehstueck", "Vegetarisch", "Vegan", "Saisonal"];
const COCKTAIL_CATS = ["Aperitif", "Spritz", "Sour", "Fruchtig", "Afterdinner", "Longdrink", "Alkoholfrei", "Seasonal", "Signature"];
const ALLERGEN_KEYS = ["gluten", "krebstiere", "eier", "fisch", "erdnuesse", "soja", "milch", "schalenfruechte", "sellerie", "senf", "sesam", "schwefeldioxid", "lupinen", "weichtiere"];

// F10 — Scale generated recipe/cocktail to requested portions
function scaleGeneratedToPortions(data, portions, tab) {
  if (!data || portions === 1 || !portions) return data;
  // Recipes carry "portions" field; cocktails carry implicit 1 portion
  if (tab === "kitchen") {
    const basePort = Number(data.portions) || 1;
    if (basePort === portions) return data;
    const factor = portions / basePort;
    return {
      ...data,
      portions,
      ingredients: (data.ingredients || []).map(ing => ({
        ...ing,
        quantity: +(Number(ing.quantity || 0) * factor).toFixed(4),
      })),
    };
  }
  // Bar: multiply quantities by requested portions (cocktails default to 1 drink)
  return {
    ...data,
    portions,
    ingredients: (data.ingredients || []).map(ing => ({
      ...ing,
      quantity: +(Number(ing.quantity || 0) * portions).toFixed(4),
    })),
  };
}

function IngredientMultiSelect({ ingredients, selected, onChange }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return ingredients.filter((i) => !ql || i.name.toLowerCase().includes(ql)).slice(0, 50);
  }, [ingredients, q]);
  const sel = new Set(selected);

  const toggle = (id) => {
    if (sel.has(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };

  return (
    <div className="relative" data-testid="ai-ingredient-select">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full bg-bg-void border border-white/10 rounded-md px-3 py-2 text-left text-sm text-ink-primary hover:border-white/20 transition-colors flex items-center justify-between"
        data-testid="ai-ingredient-toggle"
      >
        <span className="truncate">
          {selected.length === 0 ? <span className="text-ink-muted">{t("ai.select_ingredients")}</span> : `${selected.length} ausgewählt`}
        </span>
        <Search size={14} className="text-ink-muted shrink-0 ml-2" />
      </button>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-bg-surface border border-white/10 rounded-md shadow-dropdown">
          <div className="p-2 border-b border-white/10">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Suchen …" className="!py-1 !text-xs" data-testid="ai-ingredient-search" />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-xs text-ink-muted text-center">Keine Treffer.</div>
            ) : filtered.map((i) => (
              <button
                key={i.id}
                type="button"
                onClick={() => toggle(i.id)}
                className="w-full flex items-center justify-between px-3 py-1.5 text-left text-xs hover:bg-white/[0.04] transition-colors"
                data-testid={`ai-ingredient-option-${i.id}`}
              >
                <span className="text-ink-primary">{i.name}</span>
                {sel.has(i.id) && <Check size={12} className="text-brand" />}
              </button>
            ))}
          </div>
          <div className="p-2 border-t border-white/10 flex items-center justify-between">
            <button type="button" onClick={() => onChange([])} className="text-[10px] text-ink-secondary hover:text-ink-primary" data-testid="ai-ingredient-clear">Alle entfernen</button>
            <button type="button" onClick={() => setOpen(false)} className="text-[10px] text-brand hover:text-brand-hover" data-testid="ai-ingredient-done">Fertig</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AllergenSelector({ value, onChange }) {
  const { t } = useTranslation();
  const sel = new Set(value);
  const toggle = (a) => {
    if (sel.has(a)) onChange(value.filter((x) => x !== a));
    else onChange([...value, a]);
  };
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="ai-avoid-allergens">
      {ALLERGEN_KEYS.map((a) => {
        const active = sel.has(a);
        return (
          <button type="button" key={a} onClick={() => toggle(a)}
            data-testid={`ai-avoid-${a}`}
            className={`px-2 py-0.5 text-[11px] rounded-md border transition-colors ${
              active ? "bg-warn/15 border-warn/40 text-warn"
                     : "bg-white/5 border-white/10 text-ink-secondary hover:text-ink-primary hover:border-white/20"
            }`}>
            {t(`allergens.${a}`)}
          </button>
        );
      })}
    </div>
  );
}

function ResultPanel({ result, mode, onSave, saving, unmatchedNote }) {
  const { t } = useTranslation();
  if (!result) return null;
  return (
    <Card className="p-6 space-y-5 fade-in" data-testid="ai-result-panel">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-1">
            {t("ai.result_title")} · {mode === "kitchen" ? (RECIPE_CATEGORY_LABELS[result.category] || result.category) : (COCKTAIL_CATEGORY_LABELS[result.category] || result.category)}
            {result.base_spirit ? ` · ${result.base_spirit}` : ""}
          </div>
          <h2 className="font-display text-2xl font-semibold tracking-tight" data-testid="ai-result-name">{result.name}</h2>
        </div>
        <Button onClick={onSave} disabled={saving} data-testid="ai-result-save">
          {saving ? <><Spinner size={12} /> {t("common.loading")}</> : <><Save size={14} /> {mode === "kitchen" ? t("ai.save_recipe") : t("ai.save_cocktail")}</>}
        </Button>
      </div>

      {result.menu_description && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-1">{t("ai.result_description")}</div>
          <p className="text-sm text-ink-secondary italic leading-relaxed">{result.menu_description}</p>
        </div>
      )}

      <div>
        <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-2">{t("ai.result_ingredients")}</div>
        <Card className="overflow-hidden">
          <table className="w-full text-sm" data-testid="ai-result-ingredients">
            <tbody>
              {(result.ingredients || []).map((it, i) => (
                <tr key={i} className="border-b border-white/5 last:border-0">
                  <td className="px-3 py-1.5 text-ink-primary">{it.name}</td>
                  <td className="px-3 py-1.5 num text-right text-ink-secondary text-xs">{formatNumber(it.quantity, 0)} {UNIT_LABELS[it.unit] || it.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {(result.preparation_steps || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-2">{t("ai.result_steps")}</div>
          <ol className="text-sm text-ink-secondary space-y-2 list-decimal list-inside marker:text-brand marker:font-mono">
            {result.preparation_steps.map((s, i) => <li key={i} className="leading-relaxed">{s}</li>)}
          </ol>
        </div>
      )}

      {result.estimated_cost_note && (
        <div className="rounded-md border border-brand/20 bg-brand/[0.04] px-3 py-2 text-xs text-ink-secondary">
          <span className="text-brand font-medium mr-1.5">{t("ai.result_cost_note")}:</span>
          {result.estimated_cost_note}
        </div>
      )}

      {unmatchedNote && (
        <div className="rounded-md border border-warn/30 bg-warn/[0.06] px-3 py-2 text-xs text-warn" data-testid="ai-unmatched-note">
          <AlertTriangle size={11} className="inline mr-1 -mt-0.5" /> {unmatchedNote}
        </div>
      )}
    </Card>
  );
}

export default function AIGeneratorPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const [tab, setTab] = useState("kitchen");
  const [ingredients, setIngredients] = useState([]);
  const [credits, setCredits] = useState({ plan_credits: 0, purchased_credits: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [savedInfo, setSavedInfo] = useState(null);
  const [intentHint, setIntentHint] = useState(null);

  // Kitchen form
  const [kCat, setKCat] = useState("Hauptgang");
  const [kStyle, setKStyle] = useState("Österreichisch");
  const [kTargetFC, setKTargetFC] = useState("30");
  const [kIngs, setKIngs] = useState([]);
  const [kAllergens, setKAllergens] = useState([]);

  // Bar form
  const [bCat, setBCat] = useState("Spritz");
  const [bSpirit, setBSpirit] = useState("");
  const [bTargetPC, setBTargetPC] = useState("20");
  const [bIngs, setBIngs] = useState([]);

  // F10 — Portion count (default 1)
  const [portions, setPortions] = useState(1);
  // F7 — Last consumed credits (badge)
  const [lastConsumed, setLastConsumed] = useState(null);

  const loadInit = async () => {
    try {
      const [c, ing] = await Promise.all([api.get("/ai/credits"), api.get("/ingredients")]);
      setCredits(c.data);
      setIngredients(ing.data.items);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { loadInit(); }, []);

  // Pre-fill from /waste or /engineering link
  useEffect(() => {
    const s = location.state;
    if (!s) return;
    if (s.intent === "waste_reuse") {
      setTab("kitchen");
      if (s.ingredient_id) setKIngs([s.ingredient_id]);
      if (s.cuisine_style) setKStyle(s.cuisine_style);
      setKCat("Vorspeise");
      setIntentHint({
        name: s.ingredient_name || "—",
        message: t("ai.waste_intent", { ingredient: s.ingredient_name || "—" }),
        title: t("ai.waste_intent_title"),
      });
      window.history.replaceState({}, "", location.pathname);
    } else if (s.intent === "reinvent_dog") {
      if (s.kind === "cocktail") {
        setTab("bar");
        if (s.category) setBCat(s.category);
      } else {
        setTab("kitchen");
        if (s.category) setKCat(s.category);
      }
      setIntentHint({
        name: s.dish_name || "—",
        message: t("ai.reinvent_intent", { dish: s.dish_name || "—", category: s.category || "" }),
        title: t("ai.reinvent_intent_title"),
      });
      window.history.replaceState({}, "", location.pathname);
    }
  }, [location.state, location.pathname, t]);

  useEffect(() => { setResult(null); setSavedInfo(null); }, [tab]);

  const generate = async () => {
    setLoading(true); setResult(null); setSavedInfo(null);
    const before = credits?.total || 0;
    try {
      let payload, url;
      if (tab === "kitchen") {
        payload = {
          category: kCat,
          cuisine_style: kStyle || "",
          target_food_cost: Number(kTargetFC) || null,
          available_ingredient_ids: kIngs,
          avoid_allergens: kAllergens,
          portions: Number(portions) || 1,  // F10
        };
        url = "/ai/generate-recipe";
      } else {
        payload = {
          category: bCat,
          base_spirit: bSpirit || "",
          target_pour_cost: Number(bTargetPC) || null,
          available_ingredient_ids: bIngs,
          portions: Number(portions) || 1,  // F10
        };
        url = "/ai/generate-cocktail";
      }
      const { data } = await api.post(url, payload);
      // F10 — scale recipe to requested portions (if backend didn't already)
      const scaledData = scaleGeneratedToPortions(data, Number(portions) || 1, tab);
      setResult(scaledData);
      setCredits(data.credits);
      // F7 — credits badge
      const after = data?.credits?.total ?? 0;
      const consumed = Math.max(0, before - after) || 1;
      setLastConsumed({ consumed, remaining: after });
      toast.success(t("ai.success_with_credits", { consumed, remaining: after,
        defaultValue: "Generiert · {{consumed}} Credit verwendet · {{remaining}} Credits verbleibend" }));
    } catch (e) {
      toast.error(formatApiError(e));
      try { const { data } = await api.get("/ai/credits"); setCredits(data); } catch {}
    } finally { setLoading(false); }
  };

  const save = async () => {
    if (!result) return;
    setSaving(true);
    try {
      const body = {
        name: result.name,
        category: result.category,
        ingredients: result.ingredients,
        preparation_steps: result.preparation_steps,
        menu_description: result.menu_description,
        estimated_cost_note: result.estimated_cost_note,
      };
      let url, total;
      if (tab === "kitchen") {
        body.portions = 4;
        url = "/ai/save-recipe";
      } else {
        body.base_spirit = result.base_spirit || "";
        url = "/ai/save-cocktail";
      }
      total = result.ingredients.length;
      const { data } = await api.post(url, body);
      const tKey = tab === "kitchen" ? "ai.saved_recipe" : "ai.saved_cocktail";
      toast.success(t(tKey, { matched: data.matched_count, total }));
      setSavedInfo({ id: data.id, matched: data.matched_count, unmatched: data.unmatched_count });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const noCredits = credits.total <= 0;

  const unmatchedNote = savedInfo && savedInfo.unmatched > 0
    ? `${savedInfo.unmatched} ${t("ai.unmatched_hint")}`
    : null;

  return (
    <div className="max-w-7xl mx-auto" data-testid="ai-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-1.5">Modul · 04</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight flex items-center gap-3">
            <Wand2 size={26} className="text-brand" /> {t("ai.title")}
          </h1>
          <p className="text-sm text-ink-secondary mt-1">{t("ai.subtitle")}</p>
        </div>
        <Card className="px-4 py-2.5 flex items-center gap-3 lift hover:border-brand/40" data-testid="ai-credits-card">
          <Sparkles size={14} className="text-brand" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-ink-secondary">{t("ai.credits_remaining")}</div>
            <div className="num text-lg font-medium" data-testid="ai-credits-total">{credits.total}</div>
          </div>
          <div className="text-[10px] text-ink-muted ml-2 max-w-[140px] leading-tight">{t("ai.credit_cost")}</div>
        </Card>
      </div>

      {/* Intent hint (e.g. coming from /waste with a top-loss ingredient) */}
      {intentHint && (
        <div className="rounded-lg border border-brand/30 bg-brand/[0.06] px-4 py-3 mb-4 flex items-start gap-3" data-testid="ai-intent-hint">
          <Lightbulb size={16} className="text-brand shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm text-ink-primary font-medium mb-0.5">{intentHint.title || t("ai.waste_intent_title")}</div>
            <div className="text-xs text-ink-secondary leading-relaxed">{intentHint.message}</div>
          </div>
          <button onClick={() => setIntentHint(null)} className="text-ink-muted hover:text-ink-primary p-0.5" data-testid="ai-intent-dismiss">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="inline-flex items-center bg-bg-surface border border-white/10 rounded-lg p-0.5 mb-6">
        <button
          type="button"
          onClick={() => setTab("kitchen")}
          data-testid="ai-tab-kitchen"
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
            tab === "kitchen" ? "bg-brand/15 text-brand" : "text-ink-secondary hover:text-ink-primary"
          }`}>
          <ChefHat size={14} /> {t("ai.tab_kitchen")}
        </button>
        <button
          type="button"
          onClick={() => setTab("bar")}
          data-testid="ai-tab-bar"
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-2 ${
            tab === "bar" ? "bg-brand/15 text-brand" : "text-ink-secondary hover:text-ink-primary"
          }`}>
          <Martini size={14} /> {t("ai.tab_bar")}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ----- Form column ----- */}
        <Card className="p-6 space-y-5" data-testid="ai-form">
          {tab === "kitchen" ? (
            <>
              <div>
                <Label>{t("ai.category")}</Label>
                <Select value={kCat} onChange={(e) => setKCat(e.target.value)} data-testid="ai-k-category">
                  {RECIPE_CATS.map((c) => <option key={c} value={c}>{RECIPE_CATEGORY_LABELS[c] || c}</option>)}
                </Select>
              </div>
              <div>
                <Label>{t("ai.cuisine_style")}</Label>
                <Input value={kStyle} onChange={(e) => setKStyle(e.target.value)} placeholder={t("ai.cuisine_placeholder")} data-testid="ai-k-style" />
              </div>
              <div>
                <Label>{t("ai.target_food_cost")}</Label>
                <Input inputMode="decimal" value={kTargetFC} onChange={(e) => setKTargetFC(e.target.value)} data-testid="ai-k-target-fc" />
              </div>
              <div>
                <Label>{t("ai.available_ingredients")}</Label>
                <IngredientMultiSelect ingredients={ingredients} selected={kIngs} onChange={setKIngs} />
                <p className="text-[11px] text-ink-muted mt-1">{t("ai.available_hint")}</p>
              </div>
              <div>
                <Label>{t("ai.avoid_allergens")}</Label>
                <AllergenSelector value={kAllergens} onChange={setKAllergens} />
              </div>
            </>
          ) : (
            <>
              <div>
                <Label>{t("ai.category")}</Label>
                <Select value={bCat} onChange={(e) => setBCat(e.target.value)} data-testid="ai-b-category">
                  {COCKTAIL_CATS.map((c) => <option key={c} value={c}>{COCKTAIL_CATEGORY_LABELS[c] || c}</option>)}
                </Select>
              </div>
              <div>
                <Label>{t("ai.base_spirit")}</Label>
                <Input value={bSpirit} onChange={(e) => setBSpirit(e.target.value)} placeholder={t("ai.base_spirit_placeholder")} data-testid="ai-b-spirit" />
              </div>
              <div>
                <Label>{t("ai.target_pour_cost")}</Label>
                <Input inputMode="decimal" value={bTargetPC} onChange={(e) => setBTargetPC(e.target.value)} data-testid="ai-b-target-pc" />
              </div>
              <div>
                <Label>{t("ai.available_ingredients")}</Label>
                <IngredientMultiSelect ingredients={ingredients} selected={bIngs} onChange={setBIngs} />
                <p className="text-[11px] text-ink-muted mt-1">{t("ai.available_hint")}</p>
              </div>
            </>
          )}

          <div className="border-t border-white/5 pt-4 space-y-3">
            {/* F10 — Portion count input */}
            <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
              <div>
                <Label className="text-[11px]">{t("ai.portions_label", "Für wie viele Portionen generieren")}</Label>
                <Input
                  type="number"
                  min={1}
                  max={50}
                  value={portions}
                  onChange={(e) => setPortions(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
                  className="h-9"
                  data-testid="ai-portions-input"
                />
              </div>
              <Button onClick={generate} disabled={loading || noCredits} data-testid="ai-generate-button">
                {loading ? (<><Spinner size={12} /> {t("ai.generating")}</>) : result ? (<><RefreshCw size={14} /> {t("ai.regenerate")}</>) : (<><Wand2 size={14} /> {t("ai.generate")}</>)}
              </Button>
            </div>

            {/* F7 — Credits consumed badge */}
            {lastConsumed && (
              <div className="rounded-md border border-brand/20 bg-brand/5 text-xs px-3 py-2 flex items-center gap-2" data-testid="ai-credits-badge">
                <Sparkles size={12} className="text-brand" />
                <span className="text-ink-secondary">
                  {t("ai.credits_consumed", { consumed: lastConsumed.consumed, remaining: lastConsumed.remaining,
                    defaultValue: "{{consumed}} Credit verwendet · {{remaining}} Credits verbleibend" })}
                </span>
              </div>
            )}
            <div className="text-[11px] text-ink-muted">{t("ai.claude_note")}</div>
          </div>

          {noCredits && (
            <div className="rounded-md border border-err/30 bg-err/10 text-err text-xs px-3 py-2" data-testid="ai-no-credits">{t("ai.no_credits")}</div>
          )}
        </Card>

        {/* ----- Result column ----- */}
        <div className="space-y-4">
          {loading ? (
            <Card className="p-10 flex flex-col items-center justify-center gap-3" data-testid="ai-loading">
              <Spinner size={28} />
              <p className="text-sm text-ink-secondary">{t("ai.generating")}</p>
              <p className="text-[11px] text-ink-muted">5–15 s</p>
            </Card>
          ) : result ? (
            <ResultPanel result={result} mode={tab} onSave={save} saving={saving} unmatchedNote={unmatchedNote} />
          ) : (
            <Card className="p-0 overflow-hidden">
              <EmptyState
                icon={Sparkles}
                title={t("ai.empty_state_title")}
                description={t("ai.empty_state_subtitle")}
              />
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
